import {
  all,
  put,
  putResolve,
  select,
  take,
  takeLatest,
  takeEvery,
  delay,
  race,
  call,
  fork,
  cancel,
  actionChannel,
  flush,
} from "redux-saga/effects";
import { buffers } from "redux-saga";

import {
  consolidateDeviceV4,
  createDeviceV4,
  getCurrentConfigSyncV4,
  getDeviceSettingsV4,
  getDeviceV4,
  getDevicesV4,
  linkDeviceV4,
  lookupDeviceId,
  START_UPDATE_SETTINGS,
  toggleDevCon,
  updateDevice,
  updateDeviceSettingsV4,
  MIGRATE_DEVICE_TO_V4,
  RUN_CONSOLIDATION,
  RELOAD_SETTINGS_V4,
  pushConsolidation,
  getAllSyncs,
  getSyncStatus,
  INITIATE_PUSH_CONSOLIDATION,
  RESTORE_DEFAULT_SETTINGS,
  REBOOT_DEVICE_V4,
  DEVICE_COMMAND_V4,
  postDefaultSettings,
  CHECK_EMERIT_DEVICES_STATUS,
  setEmeritV4Statuses,
  v4ArcDeviceLinksLoaded,
  v4ArcDeviceLinkChecked,
  REFRESH_DEVICE_LOCATION,
  setDeviceLocation,
  RECONCILE_DEVICE_STATUS,
  RESYNC_V4_DEVICE_STATUSES,
  resyncV4DeviceStatuses,
  setDeviceAvailability,
  setDeviceOnline,
  SYNC_RUN_EVENT,
} from "../actions/deviceV4.actions";
import { openSnackbar } from "../actions/snackbar.actions";
import { DEVICES, FETCH_V4_ONLY_DEVICES, FETCH_V4_ARC_DEVICE_LINKS, CHECK_V4_ARC_DEVICE_LINK, SEND_MESSAGE } from "../constants/device.constants";
import { v4OnlyDevicesLoaded } from "../actions/device.actions";
import api, {isV4Uuid, v4ScopeFor, v4ScopeForAccountId, v4ScopeForUuid, v4SessionSafe} from "../../api/V4client";
import { getI18n } from "react-i18next";
import { deviceTypesInv, v4DeviceTypeMap, setV4DeviceTypeDefs, getV4DeviceTypeDefs } from "../../utils/models/DeviceType";
import Device from "../../utils/models/Device";
import { latestLocationByDevice, mapV4DeviceToV3, mapV4LocationToV3Position } from "../../containers/V4ARC/transforms";
import { ADMIN_PAGE } from "../../containers/AdminPage/constants";
import { selectCurrentUser } from "../selectors/app.selectors";
import {
  collectChangedSettings,
  generateDefaultValues,
  humanizeSettingKey,
  settingNamesInCategories,
} from "../../containers/DevConV4/utils";
import { ARC_MANAGED_CATEGORIES } from "../../containers/DevConV4/constants";
import { isHandledByArc } from "../../utils/arcLink";
import { findV4DeviceUuid } from "../../utils/deviceMerge";
import { V4_NATIVE_ACCOUNT } from "../../utils/V4AlarmWebSocket";

/**
 * Fetches the latest V4 location for each device and attaches it as a
 * v3-shaped `position` field on the corresponding plain device object — no
 * V4 device carries a position on the device endpoint. `locationsList`
 * answers in one call, but only for devices that reported a fix since
 * locations gained an `owner_id`; the rest still need `locationsDetail`.
 */
export function* attachV4Locations(v3DeviceObjects, v4Scope) {
  if (!v3DeviceObjects.length) return v3DeviceObjects;
  // Session-safe: the list is only a shortcut, every device can still be asked.
  const latest = yield call(() =>
    api
      .locationsList(v4SessionSafe(v4Scope))
      .then((res) => latestLocationByDevice(res?.data))
      .catch(() => new Map())
  );
  const unlisted = v3DeviceObjects.filter((d) => !latest.has(d.id));
  const locationResults = yield all(
    unlisted.map((d) =>
      call(() =>
        api
          .locationsDetail(d.id, { limit: 1 }, v4Scope)
          .then((res) => res?.data?.[0] || null)
          .catch(() => null)
      )
    )
  );
  unlisted.forEach((d, i) => latest.set(d.id, locationResults[i]));
  v3DeviceObjects.forEach((d) => {
    const position = mapV4LocationToV3Position(latest.get(d.id));
    if (position) d.position = position;
  });
  return v3DeviceObjects;
}

/**
 * Live-updates a single device's map position from a V4 websocket location
 * event. The event carries only the device uuid, so we fetch its latest fix
 * (limit=1) and write it into the device's `_position`. Skips the fetch when
 * the device isn't in state (nothing on the map to move).
 */
export function* refreshDeviceLocationSaga({ uuid, accountId }) {
  if (!uuid) return;

  const device = yield select((state) => {
    const dev = state[DEVICES];
    if (!dev) return null;
    const matches = (d) => !!d && (d.uuid === uuid || d.id === uuid);
    const selected = dev.get("selectedDevice");
    return (
      dev.get("data")?.find(matches) ||
      dev.get("v4OnlyDevices")?.find(matches) ||
      (matches(selected) ? selected : null)
    );
  });
  if (!device) return;

  // Prefer the device's own V4 owner scope; fall back to the account scope.
  const ownerUuid = device.get?.("v4OwnerId") || device.v4OwnerId;
  if (!ownerUuid && !accountId) return;

  try {
    const scope = ownerUuid
      ? v4ScopeForUuid(ownerUuid)
      : yield call(() => v4ScopeFor(accountId));
    const res = yield call(() =>
      api.locationsDetail(uuid, { limit: 1 }, scope)
    );
    const position = mapV4LocationToV3Position(res?.data?.[0] || null);
    if (position) yield put(setDeviceLocation(uuid, position));
  } catch (e) {
    console.warn(`Failed to refresh location for ${uuid}:`, e);
  }
}

// Connection events can arrive out of order or not at all, so REST `online` (what
// a reload shows) decides; an event only prompts these re-reads, ~5 min in total.
const STATUS_RECHECK_DELAYS_MS = [3, 7, 10, 20, 20, 30, 30, 60, 60, 60].map(
  (seconds) => seconds * 1000
);
// REST may be slow to follow a disconnect.
const ONLINE_READS_AFTER_DISCONNECT = 3;
// A sign of life from a device shown offline gets one read, backing off while it
// keeps answering offline.
const STATUS_HINT_DELAYS_MS = [3000];
const STATUS_HINT_INTERVAL_MS = 60 * 1000;
const STATUS_HINT_MAX_INTERVAL_MS = 15 * 60 * 1000;
// Beyond this, one list read serves the burst.
const MAX_STATUS_CHECKS = 20;
const STATUS_RESYNC_DELAY_MS = 5000;

// uuid -> { task, seq }; `seq` orders a connection event against a list read.
const statusChecks = new Map();
// uuid -> { at, wait }
const statusHints = new Map();
let statusCheckSeq = 0;

const isChecking = (uuid) => !!statusChecks.get(uuid)?.task?.isRunning();

const selectV4Device = (state, uuid) => {
  const matches = (d) => !!d && d.isFromV4 && (d.uuid === uuid || d.id === uuid);
  const dev = state[DEVICES];
  const selected = dev?.get("selectedDevice");
  return (
    dev?.get("data")?.find(matches) ||
    dev?.get("v4OnlyDevices")?.find(matches) ||
    (matches(selected) ? selected : null) ||
    // Sub-account devices live only in the admin grid.
    state[ADMIN_PAGE]?.get("devices")?.find(matches) ||
    null
  );
};

const v4RowsById = (...lists) => {
  const rows = new Map();
  lists.forEach((list) =>
    list?.forEach((d) => {
      if (d?.isFromV4 && !rows.has(d.id)) rows.set(d.id, d);
    })
  );
  return rows;
};

const selectV4Rows = (state) => {
  const dev = state[DEVICES];
  return v4RowsById(dev?.get("data"), dev?.get("v4OnlyDevices"), [
    dev?.get("selectedDevice"),
  ]);
};

const selectAdminV4Rows = (state) =>
  v4RowsById(state[ADMIN_PAGE]?.get("devices"));

// null when the device can't be read.
function* readV4Device(uuid, device, accountId) {
  try {
    const ownerUuid = device.get("v4OwnerId");
    const scope = ownerUuid
      ? v4ScopeForUuid(ownerUuid)
      : accountId && accountId !== V4_NATIVE_ACCOUNT
      ? yield call(v4ScopeForAccountId, accountId)
      : undefined;
    // Session-safe: a refused background read must not end the session.
    const res = yield call(() => api.devicesDetail(uuid, v4SessionSafe(scope)));
    return res?.data?.id ? res.data : null;
  } catch (e) {
    return null;
  }
}

// With `row`, dispatches only what differs; without, always (the admin grid's
// copy of a row can differ from the one found first).
function* applyV4DeviceStatus(v4Device, row, keepAvailability) {
  const next = mapV4DeviceToV3(v4Device);
  if (
    !row ||
    next.status !== row.get("status") ||
    (next.lastUpdate && next.lastUpdate !== row.get("lastUpdate")) ||
    next.disconnectReason !== row.get("disconnectReason")
  ) {
    yield put(
      setDeviceOnline(
        v4Device.id,
        next.status === "online",
        next.lastUpdate,
        next.disconnectReason
      )
    );
  }
  if (!keepAvailability && (!row || next.available !== row.get("available"))) {
    yield put(setDeviceAvailability(v4Device.id, next.available));
  }
}

// `expected`: what the connection event claimed; undefined for a sign of life.
export function* reconcileDeviceStatusSaga({ uuid, expected, accountId }) {
  try {
    const hint = expected == null;
    const delays = hint ? STATUS_HINT_DELAYS_MS : STATUS_RECHECK_DELAYS_MS;
    let onlineReads = 0;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      yield delay(delays[attempt]);
      const device = yield select(selectV4Device, uuid);
      if (!device) return;
      const v4Device = yield call(readV4Device, uuid, device, accountId);
      if (!v4Device) continue;
      const online = !!v4Device.online;
      if (hint) {
        const asked = statusHints.get(uuid);
        if (online) statusHints.delete(uuid);
        else if (asked) {
          asked.wait = Math.min(asked.wait * 2, STATUS_HINT_MAX_INTERVAL_MS);
        }
        if (!online) return;
      }
      // The read model can trail the event: don't trust a first read against it.
      if (!hint && attempt === 0 && online !== expected) continue;
      // An availability event during the read is newer than the read.
      const current = yield select(selectV4Device, uuid);
      const availabilityMoved =
        !current || current.get("available") !== device.get("available");
      yield* applyV4DeviceStatus(v4Device, undefined, availabilityMoved);
      onlineReads = online ? onlineReads + 1 : 0;
      if (
        online &&
        (expected !== false || onlineReads >= ONLINE_READS_AFTER_DISCONNECT)
      ) {
        return;
      }
    }
  } catch (e) {
    console.warn(`Failed to reconcile status for ${uuid}:`, e);
  }
}

// One check per device — takeLatest would let one device cancel another's.
export function* watchDeviceStatusChecks() {
  while (true) {
    const action = yield take(RECONCILE_DEVICE_STATUS);
    try {
      const { uuid, expected, accountId } = action;
      if (!uuid) continue;
      const hint = expected == null;
      const now = Date.now();
      if (hint) {
        if (isChecking(uuid)) continue;
        const device = yield select(selectV4Device, uuid);
        if (!device || device.connected) continue;
        const asked = statusHints.get(uuid);
        if (asked && now - asked.at < asked.wait) continue;
      } else {
        const running = statusChecks.get(uuid)?.task;
        if (running) yield cancel(running);
        statusHints.delete(uuid);
      }
      const busy = [...statusChecks.keys()].filter(isChecking);
      if (busy.length >= MAX_STATUS_CHECKS) {
        // A list read already under way must not undo the event.
        if (!hint) statusChecks.set(uuid, { task: null, seq: ++statusCheckSeq });
        yield put(resyncV4DeviceStatuses(accountId));
        continue;
      }
      if (hint) {
        statusHints.set(uuid, {
          at: now,
          wait: statusHints.get(uuid)?.wait || STATUS_HINT_INTERVAL_MS,
        });
      }
      const task = yield fork(reconcileDeviceStatusSaga, action);
      statusChecks.set(uuid, { task, seq: ++statusCheckSeq });
    } catch (e) {
      console.warn("Failed to start a device status check:", e);
    }
  }
}

// Skips devices with their own check (now or when the read began) or a newer event.
function* resyncV4RowsFromList(scope, selectRows, mark, checking) {
  try {
    const res = yield call(() => api.devicesList(v4SessionSafe(scope)));
    const rows = yield select(selectRows);
    for (const v4Device of Array.isArray(res?.data) ? res.data : []) {
      const row = rows.get(v4Device.id);
      if (
        !row ||
        checking.has(v4Device.id) ||
        isChecking(v4Device.id) ||
        statusChecks.get(v4Device.id)?.seq > mark
      ) {
        continue;
      }
      yield* applyV4DeviceStatus(v4Device, row);
    }
  } catch (e) {
    console.warn("Failed to resync V4 device statuses:", e);
  }
}

// One list read: after the websocket (re)connects, and for bursts past the cap.
export function* resyncV4DeviceStatusesSaga({ accountId }) {
  try {
    const mark = statusCheckSeq;
    const checking = new Set([...statusChecks.keys()].filter(isChecking));
    const native = !accountId || accountId === V4_NATIVE_ACCOUNT;
    if ((yield select(selectV4Rows)).size) {
      const scope = native
        ? undefined
        : yield call(v4ScopeForAccountId, accountId);
      yield* resyncV4RowsFromList(scope, selectV4Rows, mark, checking);
    }
    // A sub-account open in the admin grid is in no list the session's scope returns.
    const gridAccount = yield select(
      (state) => state[ADMIN_PAGE]?.get("selectedAccount")?.id
    );
    if (native && gridAccount && (yield select(selectAdminV4Rows)).size) {
      yield* resyncV4RowsFromList(
        v4ScopeForUuid(gridAccount),
        selectAdminV4Rows,
        mark,
        checking
      );
    }
  } catch (e) {
    console.warn("Failed to resync V4 device statuses:", e);
  }
}

// Batches a burst of requests into one read; requests during a read get the next.
export function* watchStatusResyncs() {
  const requests = yield actionChannel(
    RESYNC_V4_DEVICE_STATUSES,
    buffers.sliding(1)
  );
  while (true) {
    const first = yield take(requests);
    yield delay(STATUS_RESYNC_DELAY_MS);
    const [latest] = yield flush(requests);
    yield call(resyncV4DeviceStatusesSaga, latest || first);
  }
}

const emeritDeviceTypeToV4Type = {
  [deviceTypesInv.EmeritEWG100]: "emerit_ewg100",
  [deviceTypesInv.EmeritEWG200]: "emerit_ewg200",
  [deviceTypesInv.EmeritConnect]: "emerit_connect",
  [deviceTypesInv.EmeritERG170]: "emerit_erg170",
  [deviceTypesInv.EmeritERG360]: "emerit_erg360",
  [deviceTypesInv.EmeritERG530]: "emerit_erg530",
  [deviceTypesInv.EmeritERG650]: "emerit_erg650",
  [deviceTypesInv.EmeritERG655]: "emerit_erg655",
  [deviceTypesInv.EmeritEIS330]: "emerit_eis330",
  [deviceTypesInv.EmeritEIS530]: "emerit_eis530",
  [deviceTypesInv.EmeritEIS655]: "emerit_eis655",
};

// Resolve the V4 device-type string for a V3 device. The V4 device-type ids line
// up 1:1 with V3 deviceTypeIds (GET /v1/devices/device-types — cached on login),
// so the registry gives the correct type for every kind (app, generic, twig_*,
// teltonika_*, emerit_*, …). The static Emerit map — then emerit_ewg100 — is only
// a fallback for the narrow window before the registry fetch has completed.
function getV4DeviceType(deviceRecord) {
  const typeId = deviceRecord?.get?.("deviceTypeId") ?? deviceRecord?.deviceTypeId;
  const fromRegistry = getV4DeviceTypeDefs().find((d) => Number(d.id) === Number(typeId))?.name;
  return fromRegistry || emeritDeviceTypeToV4Type[typeId] || "emerit_ewg100";
}

// The cuebly-mobile types are V4-native (app-user-id-bound, no V3 source), so they
// must never be shadow-created from a V3 row. Every other registry type migrates,
// including legacy `app`.
const V4_UNMIGRATABLE_TYPES = new Set(["mobile-v2", "mobile-v2-lite"]);

// True when the device's V3 type resolves to a real, migratable V4 type in the
// registry — i.e. we can safely create it on the fly. Emerit is gated separately
// by callers (it migrates even before the registry has loaded).
export function isDeviceTypeMigratableToV4(deviceRecord) {
  const typeId = deviceRecord?.get?.("deviceTypeId") ?? deviceRecord?.deviceTypeId;
  const name = getV4DeviceTypeDefs().find((d) => Number(d.id) === Number(typeId))?.name;
  return !!name && !V4_UNMIGRATABLE_TYPES.has(name);
}

// V4 errors arrive as objects; render them as a readable string.
function formatV4Error(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (Array.isArray(err.validation_errors) && err.validation_errors.length) {
    return err.validation_errors.join(", ");
  }
  return err.message || err.error || JSON.stringify(err);
}

function isImeiAlreadyExists(createResponse) {
  const errors = createResponse?.error?.validation_errors;
  return (
    Array.isArray(errors) && errors.includes("device_with_imei_already_exists")
  );
}

export function* findV4DeviceByImei(imei, scope) {
  if (!imei) return null;
  const resp = yield putResolve(getDevicesV4(scope));
  if (resp?.error) return null;
  const devices = Array.isArray(resp) ? resp : resp?.data || [];
  const wanted = String(imei);
  return (
    devices.find(
      (d) =>
        String(d?.metadata?.imei ?? d?.metadata?.unique_id ?? "") === wanted
    ) || null
  );
}

function* selectCurrentDeviceSaga() {
  return yield select((state) => state[DEVICES]?.get("selectedDevice"));
}

// getAllSyncs() ordering isn't guaranteed and the body may be enveloped — take
// the newest run, not [0], which can be one that finished long ago.
const latestRunId = (history) => {
  const list = Array.isArray(history) ? history : history?.data || [];
  if (!list.length) return undefined;
  return [...list].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  )[0]?.id;
};

// We stopped waiting; the platform never reported a failure. Callers must check
// the real outcome before telling the user anything went wrong.
const inconclusive = (message) => {
  const e = new Error(message);
  e.inconclusive = true;
  return e;
};

export function* runConsolidationSaga({ deviceId }, strategy = "pull") {
  try {
    yield put(
      updateDevice({
        consolidationError: null,
      })
    );

    yield putResolve(postDefaultSettings(deviceId));

    const result = yield putResolve(consolidateDeviceV4(deviceId, strategy));

    if (result.error && result.status !== 409) {
      throw new Error("Error running consolidation");
    }

    // Track the run by id (ConsolidateConfigResponse); on a 409, or a 2xx with
    // no body, fall back to the latest run.
    let syncId = result?.sync_run_id || result?.id;
    if (!syncId) {
      syncId = latestRunId(yield putResolve(getAllSyncs(deviceId)));
    }

    if (!syncId) {
      throw inconclusive(
        "Device consolidation is taking longer than expected. Please contact support."
      );
    }

    const { run, timeout } = yield race({
      run: waitForSyncRun(deviceId, syncId),
      timeout: delay(60000),
    });

    if (timeout || !run) {
      throw inconclusive(
        "Device consolidation is taking longer than expected. Please contact support."
      );
    }

    if (strategy === "push") {
      return yield call(runConsolidationSaga, { deviceId }, "pull");
    }

    return { success: true };
  } catch (e) {
    return {
      error: e.message,
      inconclusive: !!e.inconclusive,
    };
  }
}

// Resolves when the V4 websocket reports this run's terminal state via
// SYNC_RUN_EVENT; created/progress events are ignored. The event is
// authoritative — we fetch the run once to enrich it (final object /
// failure_reason). Returns the run on success; throws on failure/cancel.
function* waitForSyncRunEvent(deviceId, syncId, scope) {
  while (true) {
    const event = yield take(
      (action) =>
        action.type === SYNC_RUN_EVENT &&
        action.deviceId === deviceId &&
        (!syncId || action.runId === syncId)
    );

    const { status } = event;
    if (
      status !== "succeeded" &&
      status !== "failed" &&
      status !== "cancelled"
    ) {
      continue;
    }

    const run = yield putResolve(getSyncStatus(deviceId, syncId, scope));
    const fetched = run && !run.error ? run : null;

    if (status === "succeeded") {
      return fetched || { id: syncId, result: "succeeded" };
    }
    if (status === "failed") {
      throw new Error(
        (fetched && fetched.failure_reason) || "Consolidation run failed"
      );
    }
    throw new Error("Sync run was cancelled");
  }
}

// Backstop cadence; the websocket event stays the fast path.
const SYNC_RUN_POLL_MS = 3000;

// Backstop for a SYNC_RUN_EVENT we never receive — socket down or reconnecting,
// scoped to another account, a topic the backend doesn't publish for this device
// type, or an event that fired before the take above was armed.
function* pollSyncRun(deviceId, syncId, scope) {
  while (true) {
    yield delay(SYNC_RUN_POLL_MS);
    const run = yield putResolve(getSyncStatus(deviceId, syncId, scope));
    // Unreadable or still running (result is "" until it ends).
    if (!run || run.error || !run.result) continue;
    if (run.result === "succeeded") return run;
    if (run.result === "failed") {
      throw new Error(run.failure_reason || "Consolidation run failed");
    }
    throw new Error("Sync run was cancelled");
  }
}

// Whichever lands first — the websocket event or a direct read of the run. Same
// return value and thrown messages as the event alone, so callers are unchanged;
// they just stop depending on one websocket frame arriving.
export function* waitForSyncRun(deviceId, syncId, scope) {
  // No run id to read: only the event can identify the run.
  if (!syncId) return yield call(waitForSyncRunEvent, deviceId, syncId, scope);

  const { event, polled } = yield race({
    event: call(waitForSyncRunEvent, deviceId, syncId, scope),
    polled: call(pollSyncRun, deviceId, syncId, scope),
  });

  return event || polled;
}

export function* consolidateAndVerify(deviceId, strategy, scope) {
  const response = yield putResolve(
    consolidateDeviceV4(deviceId, strategy, false, scope)
  );

  if (response?.error && response.status !== 409) {
    throw new Error(`Failed to start ${strategy} consolidation`);
  }

  let syncId = response?.sync_run_id || response?.id;

  if (!syncId) {
    // 409: a run is already in flight — fall back to the latest run id.
    syncId = latestRunId(yield putResolve(getAllSyncs(deviceId, scope)));
  }

  if (!syncId) {
    throw new Error(`No sync run returned for ${strategy} consolidation`);
  }

  const { run, timeout } = yield race({
    run: waitForSyncRun(deviceId, syncId, scope),
    timeout: delay(60000),
  });

  if (timeout || !run) {
    throw new Error(
      "Device consolidation is taking longer than expected. Please contact support."
    );
  }

  return run;
}

// Emerit onboarding: stage defaults → push (verify) → pull (verify).
export function* onboardEmeritConsolidationSaga(deviceId, scope) {
  try {
    yield put(updateDevice({ consolidationError: null }));

    yield putResolve(postDefaultSettings(deviceId, scope));

    yield call(consolidateAndVerify, deviceId, "push", scope);

    yield call(consolidateAndVerify, deviceId, "pull", scope);

    return { success: true };
  } catch (e) {
    return { error: e.message };
  }
}

export function* linkDevice(deviceId, scope) {
  const result = yield putResolve(linkDeviceV4(deviceId, scope));

  if (result.error) {
    // TODO: device is already linked we show an error saying that user needs to contact the admin
    throw new Error(result.error);
  }

  return result;
}

/**
 * Ensures a device is migrated to V4. Returns the V4 UUID.
 * Performs: lookup → if not in V4, create → (emerit-only) link + consolidation.
 * Throws on failure.
 */
/**
 * Lightweight lookup: returns the V4 UUID for a device.
 * Only does lookup + create if missing. No link, no settings, no consolidation.
 * Use this for replication targets where we just need the UUID.
 * `scope` (a headers object from v4ScopeFor) creates the device under a specific
 * V4 account owner instead of the token-default scope.
 * `providedRecord` supplies the V3 device record for the create payload when the
 * Redux `data` list may not contain it (e.g. the ManageARCDialog flow).
 */
export function* lookupOrCreateDeviceV4(deviceId, scope, providedRecord) {
  const lookupResponse = yield putResolve(lookupDeviceId(deviceId));
  const uuid = lookupResponse?.uuid;

  if (!uuid) {
    throw new Error(`Device ${deviceId} could not be looked up in V4`);
  }

  const v4Device = yield putResolve(getDeviceV4(uuid, scope));

  if (v4Device.error) {
    // Prefer the caller-supplied record — the Redux `data` list can miss the
    // device (yielding a `device_<id>` name and no imei, which breaks dedup).
    let deviceRecord = providedRecord;
    if (!deviceRecord) {
      const devices = yield select((state) => state[DEVICES]?.get("data")?.toList());
      deviceRecord = devices?.find((d) => d.id === deviceId);
    }

    const createResponse = yield putResolve(
      createDeviceV4({
        id: uuid,
        name: deviceRecord?.get?.("name") || `device_${deviceId}`,
        type: getV4DeviceType(deviceRecord),
        metadata: {
          imei: deviceRecord?.get?.("uniqueId"),
        },
      }, scope)
    );

    if (createResponse.error) {
      throw new Error(`Failed to create V4 device for ${deviceId}: ${createResponse.error}`);
    }
  }

  return uuid;
}

// V3 device -> V4 uuid, creating nothing: its own uuid, a match in the V4 list,
// then the migration lookup. Never the V3 numeric id.
export function* resolveV4DeviceUuid(device) {
  const v4Only = yield select((state) => state[DEVICES]?.get("v4OnlyDevices"));
  const fromList = findV4DeviceUuid(device, v4Only);
  if (fromList) return fromList;
  try {
    const res = yield api.migrationLookupDetail(`device_${device.id}`);
    return res?.data?.uuid || null;
  } catch (e) {
    return null;
  }
}

// As resolveV4DeviceUuid, but a missing device is migrated into V4 when its type
// can be: Emerit always, others once the device-types registry knows them. The
// cuebly-mobile types are only verified, never created.
export function* ensureV4DeviceUuid(device) {
  const v4Only = yield select((state) => state[DEVICES]?.get("v4OnlyDevices"));
  const fromList = findV4DeviceUuid(device, v4Only);
  if (fromList) return fromList;

  // Without the account's scope, the token's default applies.
  let scope;
  const accountId = device.get?.("accountId");
  if (accountId) {
    try {
      const resolved = yield call(() => v4ScopeForAccountId(accountId));
      scope = resolved?.headers;
    } catch (e) {
      // the account has no V4 mapping
    }
  }

  if (device.isEmerit || isDeviceTypeMigratableToV4(device)) {
    // The record supplies name, type and imei; without the imei V3/V4 dedup breaks.
    return yield call(lookupOrCreateDeviceV4, device.id, scope, device);
  }

  const uuid = yield* resolveV4DeviceUuid(device);
  if (!uuid) return null;
  const v4Device = yield putResolve(getDeviceV4(uuid, scope));
  return v4Device?.error ? null : uuid;
}

export function* ensureDeviceMigratedToV4(deviceId) {
  const lookupResponse = yield putResolve(lookupDeviceId(deviceId));
  const uuid = lookupResponse?.uuid;

  if (!uuid) {
    throw new Error(`Device ${deviceId} could not be looked up in V4`);
  }

  // Fetch the device record from Redux for proper name/metadata
  const devices = yield select((state) => state[DEVICES]?.get("data")?.toList());
  const deviceRecord = devices?.find((d) => d.id === deviceId);
  const isEmerit = !!deviceRecord?.isEmerit;

  const v4Device = yield putResolve(getDeviceV4(uuid));

  if (v4Device.error) {
    // Device doesn't exist in V4 yet — create it
    const createResponse = yield putResolve(
      createDeviceV4({
        id: uuid,
        name: deviceRecord?.get?.("name") || `device_${deviceId}`,
        type: getV4DeviceType(deviceRecord),
        metadata: {
          imei: deviceRecord?.get?.("uniqueId"),
        },
      })
    );

    if (createResponse.error) {
      throw new Error(`Failed to create V4 device for ${deviceId}: ${createResponse.error}`);
    }

    // emerit-link is only for Emerit devices — non-fatal if it fails
    if (isEmerit) {
      try {
        yield linkDevice(uuid);
      } catch (e) {
        console.warn(`emerit-link failed for ${deviceId}, continuing:`, e.message);
      }
    }
  } else if (isEmerit && !v4Device?.metadata?.["emerit-ref"]) {
    try {
      yield linkDevice(uuid);
    } catch (e) {
      console.warn(`emerit-link failed for ${deviceId}, continuing:`, e.message);
    }
  }

  // Ensure config settings are synced — run consolidation if needed
  const settings = yield putResolve(getDeviceSettingsV4(uuid));
  if (settings.error) {
    const consolidationResult = yield call(
      runConsolidationSaga,
      { deviceId: uuid },
      isEmerit ? "push" : "pull"
    );

    if (!consolidationResult.success) {
      throw new Error(
        `Consolidation failed for device ${deviceId}: ${consolidationResult.error}`
      );
    }
  }

  return uuid;
}

export function* migrateDeviceToV4Saga({ deviceId }) {
  try {
    const currentDevice = yield selectCurrentDeviceSaga();
    if (!currentDevice) return;

    // V4-native devices (e.g. V4 Twig) — fetch settings directly; surface
    // any error instead of auto-running a pull consolidation.
    if (currentDevice.isFromV4 && !currentDevice.isEmerit) {
      const uuid = currentDevice.get("uuid") || currentDevice.get("id");
      if (!uuid) return;

      yield put(
        updateDevice({
          isMigratingV4: true,
          migrationError: null,
          consolidationError: null,
        })
      );

      const settings = yield putResolve(getDeviceSettingsV4(uuid));

      if (settings?.error) {
        yield put(
          updateDevice({
            isMigratingV4: false,
            migrationError:
              "Device configuration is not yet synced with the platform. Please ensure the device is online and try again later.",
          })
        );
        return;
      }

      yield put(updateDevice({ isMigratingV4: false }));
      return;
    }

    if (!currentDevice.isEmerit) {
      return;
    }

    yield put(
      updateDevice({
        isMigratingV4: true,
        migrationError: null,
        consolidationError: null,
      })
    );

    // Pin every V4 call below to the device's account so the device is created
    // (and linked/consolidated) under the right owner. Non-fatal if the account
    // has no V4 mapping — we fall back to the token's default scope.
    const v3AccountId = currentDevice.get("accountId");
    let scope;
    if (v3AccountId) {
      try {
        const resolved = yield call(() => v4ScopeFor(v3AccountId));
        scope = resolved?.headers;
      } catch (e) {
        console.warn(
          `Could not resolve V4 account scope for ${v3AccountId}:`,
          e.message
        );
      }
    }

    let uuid = currentDevice.get("uuid");

    if (!currentDevice.get("uuid")) {
      const lookupResponse = yield putResolve(lookupDeviceId(deviceId));

      uuid = lookupResponse.uuid;

      yield put(updateDevice({ uuid }));
    }

    if (!uuid) {
      throw new Error("Failed to create device");
    }

    let device = yield putResolve(getDeviceV4(uuid, scope));

    // True once we emerit-link in this pass (fresh create, or an existing/
    // adopted device still missing its emerit-ref). A newly onboarded device
    // must run the consolidation; an already-onboarded one (re-opened panel)
    // must not, or we'd overwrite its settings with defaults.
    let linkedThisPass = false;

    if (device.error) {
      const createDeviceResponse = yield putResolve(
        createDeviceV4(
          {
            id: uuid,
            name: currentDevice.get("name"),
            type: getV4DeviceType(currentDevice),
            metadata: {
              imei: currentDevice.get("uniqueId"),
              ...(v3AccountId
                ? { "v3-account-id": String(v3AccountId) }
                : {}),
            },
          },
          scope
        )
      );

      if (createDeviceResponse.error) {
        // IMEI already registered: device exists under another uuid — adopt it.
        const existing = isImeiAlreadyExists(createDeviceResponse)
          ? yield call(
              findV4DeviceByImei,
              currentDevice.get("uniqueId"),
              scope
            )
          : null;

        if (!existing?.id) {
          throw new Error(
            "Failed to create device: " +
              formatV4Error(createDeviceResponse.error)
          );
        }

        uuid = existing.id;
        yield put(updateDevice({ uuid }));
        device = existing;
      } else {
        yield linkDevice(uuid, scope);
        linkedThisPass = true;
        device = null;
      }
    }

    if (device && !device.error && !device?.metadata?.["emerit-ref"]) {
      yield linkDevice(uuid, scope);
      linkedThisPass = true;
    }

    const settings = yield putResolve(getDeviceSettingsV4(uuid, scope));

    // Run the 2-step consolidation for a device onboarded this pass; keep
    // settings.error as a fallback for a previously-linked device that never
    // synced.
    if (linkedThisPass || settings.error) {
      const consolidationResult = yield call(
        onboardEmeritConsolidationSaga,
        uuid,
        scope
      );

      if (!consolidationResult.success) {
        yield put(
          updateDevice({
            isMigratingV4: false,
            consolidationError: consolidationResult.error,
            migrationError:
              "Device consolidation is taking longer than expected. Please contact support.",
          })
        );
        return;
      }

      yield putResolve(getDeviceSettingsV4(uuid, scope));
    }

    yield put(
      updateDevice({
        migratedV4: true,
        isMigratingV4: false,
      })
    );

    // Onboarding succeeded → the device is in V4 and linked. Clear any stale
    // "incomplete Emerit" badge right away.
    yield put(
      setEmeritV4Statuses({
        [deviceId]: { v4Exists: true, hasEmeritRef: true },
      })
    );
  } catch (e) {
    yield put(
      updateDevice({
        isMigratingV4: false,
        migrationError: e.message,
      })
    );
  }
}

// Exported so pushes outside this saga map the same codes to the same message.
export const UPDATE_SETTINGS_ERROR_CODES_TO_RUN_CONSOLIDATION = {
  source_integrity_failed: "source_integrity_failed",
  diff_failed: "diff_failed",
  target_integrity_invalid: "target_integrity_invalid",
  could_not_write_settings: "could_not_write_settings",
  final_verify_failed: "final_verify_failed",
  target_integrity_failed: "target_integrity_failed",
  process_timeout: "process_timeout",
};

function* arcManagedSettingNames(device) {
  const user = yield select(selectCurrentUser());
  const deviceState = yield select((state) => state[DEVICES]);
  const handled = isHandledByArc(
    device,
    user,
    deviceState?.get("v4ArcDeviceIds")
  );
  return handled
    ? settingNamesInCategories(deviceState?.get("devConV4"), ARC_MANAGED_CATEGORIES)
    : new Set();
}

const settingsNotSavedMessage = (names) => {
  const i18n = getI18n();
  return i18n.t("failedToSaveSettings", {
    settings: names
      .map((name) => i18n.t(name, { defaultValue: humanizeSettingKey(name) }))
      .join(", "),
    defaultValue: "Couldn't save: {settings}",
  });
};

export function* startUpdateSettingsSaga({ settings }) {
  const currentDevice = yield selectCurrentDeviceSaga();

  if (!currentDevice || !currentDevice?.get("uuid")) {
    yield put(toggleDevCon(false));
    return;
  }

  const uuid = currentDevice.get("uuid");

  if (!uuid) {
    yield put(toggleDevCon(false));
    return;
  }

  // Never write ARC-managed settings (emergency numbers).
  const arcManaged = yield* arcManagedSettingNames(currentDevice);
  const writes = Object.entries(settings).filter(
    ([name]) => !arcManaged.has(name)
  );
  if (writes.length < Object.keys(settings).length) {
    console.warn("Skipped writing ARC-managed settings of", uuid);
  }
  if (!writes.length) return;

  yield put(
    updateDevice({
      updatingSettingError: null,
    })
  );

  let failed = writes;
  try {
    const responses = yield all(
      writes.map(([name, value]) =>
        putResolve(
          updateDeviceSettingsV4(uuid, name, {
            value: JSON.stringify(value),
          })
        )
      )
    );
    failed = writes.filter((_, i) => responses[i]?.error);

    const errors = responses.map((res) => res?.error).filter(Boolean);
    const needsConsolidation = errors.some(
      (error) => !!UPDATE_SETTINGS_ERROR_CODES_TO_RUN_CONSOLIDATION[error]
    );

    if (needsConsolidation) {
      yield put(
        updateDevice({
          updatingSettingError: "Needs consolidation",
        })
      );
      yield put(
        openSnackbar(
          "Failed to update device settings, please run consolidation"
        )
      );
      // A batch can fail in part.
      yield putResolve(getDeviceSettingsV4(uuid));
      return;
    }

    if (errors.length) {
      throw new Error(errors.join(", "));
    }

    yield putResolve(getDeviceSettingsV4(uuid));
  } catch (error) {
    yield put(
      updateDevice({
        updatingSettingError:
          "Failed to update device settings: " + error.message,
      })
    );
    yield put(
      openSnackbar(settingsNotSavedMessage(failed.map(([name]) => name)))
    );
    yield putResolve(getDeviceSettingsV4(uuid));
  }
}

export function* pushConsolidationSaga({ deviceId: uuid, targetDeviceIds, reboot }) {
  yield put(updateDevice({ isSynchronising: true, consolidationError: null }));

  try {
    const pushResponse = yield putResolve(pushConsolidation(uuid, reboot));

    if (pushResponse?.error) {
      throw new Error("Consolidation failed " + pushResponse.error);
    }

    // Track the run we created by id — getAllSyncs() ordering isn't guaranteed.
    const syncId = pushResponse?.sync_run_id || pushResponse?.id;
    if (!syncId) {
      throw new Error("No sync run returned for push consolidation");
    }

    // Wait for the run to resolve (no cap) so the in-progress spinner stays
    // through a long sync; the backend run ends it.
    yield call(waitForSyncRun, uuid, syncId);

    // Refresh settings until the indicators clear (capped) — reported_state can
    // lag a succeeded run.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (attempt > 0) yield delay(1000);
      const resp = yield putResolve(getDeviceSettingsV4(uuid));
      const settingsMap = resp?.settings;
      if (!settingsMap) break;
      const remaining = collectChangedSettings(
        settingsMap,
        generateDefaultValues(settingsMap)
      ).size;
      if (remaining === 0) break;
    }

    // Replicate to target devices if multi-device selection was made
    // targetDeviceIds holds V3 ids and V4 uuids; filter out the source's own
    const currentDevice = yield selectCurrentDeviceSaga();
    const sourceId = currentDevice?.get("id");
    const targetIds = (targetDeviceIds?.toArray?.() || targetDeviceIds || [])
      .filter(id => id !== sourceId);
    const failedDevices = [];
    if (targetIds.length > 0) {
      for (const targetId of targetIds) {
        try {
          // The V3 lookup would derive a different uuid from a V4 one.
          const targetUuid = isV4Uuid(targetId)
            ? targetId
            : yield call(lookupOrCreateDeviceV4, targetId);
          yield call(() => api.deviceConfigReplicateCreate(uuid, targetUuid, { type: "full" }));
        } catch (e) {
          console.warn(`Replicate failed for device ${targetId}:`, e);
          failedDevices.push(targetId);
        }
      }
    }

    if (failedDevices.length > 0) {
      yield put(openSnackbar(`Settings synced, but replication failed for ${failedDevices.length} device(s)`, "warning"));
    } else {
      yield put(
        openSnackbar(
          reboot
            ? "Settings synced — the device will reboot"
            : "Settings synced successfully",
          "success"
        )
      );
    }
  } catch (e) {
    yield put(
      updateDevice({
        consolidationError: e.message,
      })
    );
    // Map the cryptic integrity codes to a readable message.
    const friendly = UPDATE_SETTINGS_ERROR_CODES_TO_RUN_CONSOLIDATION[e.message]
      ? "Couldn't sync the configuration to the device — it's out of sync with the platform. Please run a consolidation and try again."
      : e.message || "Failed to sync settings to the device";
    yield put(openSnackbar(friendly));
  } finally {
    yield put(updateDevice({ isSynchronising: false }));
  }
}

// Generic V4 command via devicesCommandCreate, scoped by the device's V4 owner.
// Owner/account can be passed explicitly by non-current-device callers.
export function* deviceCommandV4Saga({ deviceId, command, ownerId, accountId }) {
  const currentDevice = yield selectCurrentDeviceSaga();
  const uuid = deviceId || currentDevice?.get("uuid");

  if (!uuid || !command) return;

  const ownerUuid = ownerId || currentDevice?.get?.("v4OwnerId");
  const acctId = accountId ?? currentDevice?.get?.("accountId");
  const i18n = getI18n();
  try {
    let scope;
    if (ownerUuid) scope = v4ScopeForUuid(ownerUuid);
    else if (acctId) scope = yield call(() => v4ScopeFor(acctId));
    // SDK 0.2.0 put an optional body before the request params.
    yield call(() => api.devicesCommandCreate(uuid, command, undefined, scope));
    yield put(openSnackbar(i18n.t("commandSent"), "success"));
  } catch (e) {
    yield put(openSnackbar(i18n.t("somethingWentWrong")));
  }
}

// Reboot entry point (DevConV4 more-menu) — delegates to the generic command.
export function* rebootDeviceV4Saga({ deviceId, ownerId, accountId }) {
  yield* deviceCommandV4Saga({ deviceId, command: "reboot", ownerId, accountId });
}

export function* restoreDefaultSettingsSaga({ deviceId }) {
  const currentDevice = yield selectCurrentDeviceSaga();
  const uuid = deviceId || currentDevice?.get("uuid");

  if (!uuid) return;

  try {
    yield put(updateDevice({ isSynchronising: true }));
    yield call(() => api.deviceConfigDefaultSettingsCreate(uuid));
    yield putResolve(getDeviceSettingsV4(uuid));
    yield put(openSnackbar("Settings restored to defaults", "success"));
  } catch (e) {
    yield put(openSnackbar("Failed to restore default settings"));
  } finally {
    yield put(updateDevice({ isSynchronising: false }));
  }
}

export function* checkEmeritDevicesStatusSaga({ deviceIds }) {
  const statuses = {};
  try {
    const deviceState = yield select((state) => state[DEVICES]);
    const data = deviceState?.get?.("data");
    const v4Only = deviceState?.get?.("v4OnlyDevices");
    // V4-native emerit devices live in v4OnlyDevices (their id is a uuid), so
    // search both stores — not just `data` — to recover their owner + uuid.
    const recordFor = (id) =>
      data?.get?.(id) ||
      data?.find?.((d) => d?.id === id) ||
      v4Only?.find?.((d) => d?.id === id) ||
      null;

    for (const deviceId of deviceIds) {
      try {
        const record = recordFor(deviceId);

        // The V4 uuid: the device's own (V4-native or already migrated), else a
        // migration lookup for a not-yet-migrated V3 device.
        let uuid = record?.get?.("uuid") || null;
        if (!uuid) {
          const lookupResponse = yield putResolve(lookupDeviceId(deviceId));
          uuid = lookupResponse?.uuid || null;
        }

        // Scope to the device's V4 owner account (V4-native records carry it)
        // or fall back to resolving its V3 account.
        const ownerUuid = record?.get?.("v4OwnerId");
        const accountId = record?.get?.("accountId");
        let scope;
        if (ownerUuid) {
          scope = v4ScopeForUuid(ownerUuid);
        } else if (accountId) {
          try {
            scope = yield call(() => v4ScopeFor(accountId));
          } catch (_) {
            // no V4 mapping — fall back to the token's default scope
          }
        }

        // V4-native records already carry the V4 metadata — use it first.
        const v4Meta = record?.get?.("v4Metadata") || null;
        let v4Device = v4Meta ? { metadata: v4Meta } : null;
        let hasEmeritRef = !!v4Meta?.["emerit-ref"];

        // The list/cached metadata is slimmed and omits emerit-ref, so confirm a
        // linked device via the full detail record when we have a uuid but no
        // emerit-ref yet (also resolves a device we hold no metadata for).
        if (uuid && !hasEmeritRef) {
          try {
            const res = yield call(() => api.devicesDetail(uuid, scope));
            if (res?.data) {
              v4Device = res.data;
              hasEmeritRef = !!res.data?.metadata?.["emerit-ref"];
            }
          } catch (_) {
            // detail fetch failed — keep what we have
          }
        }

        statuses[deviceId] = v4Device
          ? { v4Exists: true, hasEmeritRef }
          : { v4Exists: false, hasEmeritRef: false };
      } catch (e) {
        statuses[deviceId] = { v4Exists: false, hasEmeritRef: false };
      }
    }
  } catch (e) {
    for (const deviceId of deviceIds) {
      statuses[deviceId] = { v4Exists: false, hasEmeritRef: false };
    }
  }
  yield put(setEmeritV4Statuses(statuses));
}

/**
 * Collects the V4 devices linked to an ARC. The link lives on the ARC
 * (`device_links`), so there is no `arcKey` on the device to read as in V3.
 * Each ARC embeds the links of the request's account scope — the session's
 * own account, or the sub-account a manager entered — which are the devices
 * on screen. A failed fetch keeps the last known set.
 */
export function* fetchV4ArcDeviceLinksSaga() {
  try {
    // Session-safe: being refused the ARC list is not an expired session.
    const res = yield call(() => api.arcsList(v4SessionSafe()));
    const deviceIds = (res?.data || []).flatMap((arc) =>
      (arc?.device_links || []).map((link) => link?.device_id).filter(Boolean)
    );
    yield put(v4ArcDeviceLinksLoaded(deviceIds));
  } catch (e) {
    console.warn("Failed to fetch V4 ARC device links:", e?.response?.status || e);
  }
}

function* readV4ArcDeviceLink(uuid, ownerId, accountId) {
  let scope;
  if (ownerId) scope = v4ScopeForUuid(ownerId);
  else if (accountId) {
    try {
      scope = yield call(() => v4ScopeForAccountId(accountId));
    } catch (_) {
      // no V4 mapping — fall back to the token's default scope
    }
  }
  // Session-safe: being refused the ARC list is not an expired session.
  const res = yield call(() => api.arcsList(v4SessionSafe(scope)));
  return (res?.data || []).some((arc) =>
    (arc?.device_links || []).some((link) => link?.device_id === uuid)
  );
}

// One device's ARC link under its owner's scope; unknown on failure or timeout.
export function* checkV4ArcDeviceLinkSaga({ uuid, ownerId, accountId }) {
  if (!uuid) return;
  try {
    // DevConV4 waits on this, so it must finish.
    const { linked } = yield race({
      linked: call(readV4ArcDeviceLink, uuid, ownerId, accountId),
      timeout: delay(15000),
    });
    if (linked === undefined) throw new Error("ARC link check timed out");
    yield put(v4ArcDeviceLinkChecked(uuid, linked));
  } catch (e) {
    console.warn("Failed to check the V4 ARC link of", uuid, e?.response?.status || e);
    yield put(v4ArcDeviceLinkChecked(uuid, null));
  }
}

// A static registry, but the seed, AUTH_SUCCESS and every WS_DATA_INIT ask: one
// shared read per page load. A failed or empty read is dropped for the next caller.
let v4DeviceTypesLoad = null;

function loadV4DeviceTypes() {
  if (!v4DeviceTypesLoad) {
    v4DeviceTypesLoad = api
      .devicesDeviceTypesList()
      .then((res) => {
        const defs = res?.data || [];
        setV4DeviceTypeDefs(defs);
        if (!defs.length) v4DeviceTypesLoad = null;
      })
      .catch((e) => {
        v4DeviceTypesLoad = null;
        console.warn("Failed to fetch V4 device types:", e);
      });
  }
  return v4DeviceTypesLoad;
}

/**
 * Fetches V4 device type definitions from the API and caches them.
 * This populates the brand lookup used by isV4OnlyType / getV4Brand.
 */
export function* fetchV4DeviceTypesSaga() {
  try {
    yield call(loadV4DeviceTypes);
  } catch (e) {
    console.warn("Failed to fetch V4 device types:", e);
  }
}

/**
 * Fetches V4-only devices (e.g. mobile-v2-lite) and stores them in Redux.
 * These are devices that only exist in V4 and have no V3 counterpart.
 * Account assignment is handled via the 'v3-account-id' metadata field
 * that is stored during device creation.
 */
export function* fetchV4OnlyDevicesSaga({ account }) {
  try {
    // V4-native grids use the admin loader; the V3 lookup can't scope a uuid.
    if ((yield select(selectCurrentUser()))?.isV4Native) return;

    // Clear V4 devices when no account is selected
    if (!account) {
      yield put(v4OnlyDevicesLoaded([]));
      return;
    }

    const v3AccountId = account.get ? account.get('id') : account.id;
    if (!v3AccountId) {
      yield put(v4OnlyDevicesLoaded([]));
      return;
    }

    // Resolve the single selected V3 account → V4 scope
    let v4Scope;
    try {
      v4Scope = yield call(() => v4ScopeFor(v3AccountId));
    } catch (e) {
      yield put(v4OnlyDevicesLoaded([]));
      return;
    }

    // Fetch V4 devices scoped to this account
    let v4Devices = [];
    try {
      const res = yield call(() => api.devicesList(v4Scope));
      v4Devices = res?.data || [];
    } catch (e) {
      // devicesList failed
    }

    v4Devices.forEach((d) => {
      if (!d.metadata?.["v3-account-id"]) {
        d.metadata = {
          ...(d.metadata || {}),
          "v3-account-id": v3AccountId.toString(),
        };
      }
    });

    const v3Devices = v4Devices.map(mapV4DeviceToV3);
    yield* attachV4Locations(v3Devices, v4Scope);
    const mapped = v3Devices.map((d) => new Device(d));
    yield put(v4OnlyDevicesLoaded(mapped));
  } catch (e) {
    console.warn("Failed to fetch V4 devices:", e);
    yield put(v4OnlyDevicesLoaded([]));
  }
}

/**
 * User-facing version of fetchV4OnlyDevicesSaga.
 * Uses the current user's accountId (instead of admin sub-accounts)
 * to fetch V4-only devices for the user's own account.
 * This is triggered by WS_DATA_INIT so V4 devices are available
 * on user-facing pages (e.g. Scenarios).
 */
export function* fetchV4OnlyDevicesForUserSaga() {
  try {
    // Skip if admin state already has accounts loaded (admin flow will handle it)
    const adminState = yield select((state) => state[ADMIN_PAGE]);
    const rawAccounts = adminState?.get?.("accounts") || [];
    const v3Accounts = rawAccounts.toArray
      ? rawAccounts.toArray()
      : Array.isArray(rawAccounts)
      ? rawAccounts
      : [];
    if (v3Accounts.length) return; // Admin flow will handle via ACCOUNTS_CHANGED

    const user = yield select(selectCurrentUser());
    if (!user || !user.accountId) return;

    // Resolve the user's V3 accountId → V4 scope
    let v4Scope;
    try {
      v4Scope = yield call(() => v4ScopeFor(user.accountId));
    } catch (e) {
      yield put(v4OnlyDevicesLoaded([]));
      return;
    }

    // Fetch V4 devices scoped to this account
    let v4Devices = [];
    try {
      const res = yield call(() => api.devicesList(v4Scope));
      v4Devices = res?.data || [];
    } catch (e) {
      // devicesList failed
    }

    v4Devices.forEach((d) => {
      if (!d.metadata?.["v3-account-id"]) {
        d.metadata = {
          ...(d.metadata || {}),
          "v3-account-id": user.accountId.toString(),
        };
      }
    });

    const v3Devices = v4Devices.map(mapV4DeviceToV3);
    yield* attachV4Locations(v3Devices, v4Scope);
    const mapped = v3Devices.map((d) => new Device(d));
    yield put(v4OnlyDevicesLoaded(mapped));
  } catch (e) {
    console.warn("Failed to fetch V4 devices for user:", e);
    yield put(v4OnlyDevicesLoaded([]));
  }
}

/**
 * Sends an OSM message to V4-only devices via the V4 SDK. V3 devices are
 * handled by the v3 sendMessage saga; we filter to V4 only here so both
 * sagas can run on the same SEND_MESSAGE action without interfering.
 * Each device is scoped by its V4 owner_id (no v3-account-id lookup needed).
 */
export function* sendMessageV4Saga({ message, devices, onResult }) {
  // devices may arrive as an Immutable.List (from selectors) or a plain array
  // (from alarm details). Normalize so `.length` works in both cases.
  const v4Devices = Array.from(devices || []).filter((d) => d?.isFromV4);
  if (!v4Devices.length) {
    // Always notify the caller so it knows this saga is done — the dialog
    // waits for both V3 and V4 sagas before deciding what to render.
    if (onResult) onResult({ success: true, errors: [] });
    return;
  }
  const errors = [];
  for (const d of v4Devices) {
    const ownerUuid = d.get?.("v4OwnerId") || d.v4OwnerId;
    if (!ownerUuid) {
      errors.push({ deviceId: d.id, name: d.name || d.id, status: null, detail: 'missing owner_id' });
      yield put(openSnackbar(`Failed to send to ${d.name || d.id}: missing owner_id`));
      continue;
    }
    try {
      const scope = v4ScopeForUuid(ownerUuid);
      yield call(() => api.devicesSendOsmCreate(d.id, { message }, scope));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.message || e?.message || "unknown error";
      console.warn(`Failed to send V4 OSM to ${d.id}`, e);
      errors.push({ deviceId: d.id, name: d.name || d.id, status, detail });
      // Suppress the snackbar for 406 ("device unavailable") only when the
      // caller has opted in via onResult — they'll render an inline error
      // instead. All other statuses keep the existing toast behavior so
      // unrelated callers / failures still surface visibly.
      const callerHandlesInline = onResult && status === 406;
      if (!callerHandlesInline) {
        yield put(openSnackbar(`Failed to send message${status ? ` (${status})` : ""}: ${detail}`));
      }
    }
  }
  if (onResult) {
    onResult({ success: errors.length === 0, errors });
  }
}

export const deviceV4Sagas = [
  takeLatest(FETCH_V4_ONLY_DEVICES, fetchV4OnlyDevicesSaga),
  takeLatest(CHECK_EMERIT_DEVICES_STATUS, checkEmeritDevicesStatusSaga),
  takeLatest(FETCH_V4_ARC_DEVICE_LINKS, fetchV4ArcDeviceLinksSaga),
  // Per-device, so takeEvery.
  takeEvery(CHECK_V4_ARC_DEVICE_LINK, checkV4ArcDeviceLinkSaga),
  takeLatest(MIGRATE_DEVICE_TO_V4, migrateDeviceToV4Saga),
  takeLatest(START_UPDATE_SETTINGS, startUpdateSettingsSaga),
  takeLatest(INITIATE_PUSH_CONSOLIDATION, pushConsolidationSaga),
  takeLatest(RESTORE_DEFAULT_SETTINGS, restoreDefaultSettingsSaga),
  takeLatest(REBOOT_DEVICE_V4, rebootDeviceV4Saga),
  takeLatest(DEVICE_COMMAND_V4, deviceCommandV4Saga),
  // Per-device, so takeEvery — concurrent fixes for different devices must not
  // cancel one another the way takeLatest would.
  takeEvery(REFRESH_DEVICE_LOCATION, refreshDeviceLocationSaga),
  fork(watchDeviceStatusChecks),
  fork(watchStatusResyncs),
  takeLatest(SEND_MESSAGE, sendMessageV4Saga),
  takeLatest(RUN_CONSOLIDATION, function* ({ deviceId, strategy = "pull" }) {
    yield put(updateDevice({ isSynchronising: true }));

    const result = yield runConsolidationSaga({ deviceId }, strategy);

    // Ask for the settings either way: the happy path needs the refresh, and a
    // give-up needs to know whether the run actually landed.
    const settings = yield putResolve(getDeviceSettingsV4(deviceId));
    const readable = !settings?.error;

    if (result.error && !(result.inconclusive && readable)) {
      yield put(
        updateDevice({
          consolidationError: result.error,
        })
      );
      yield put(
        openSnackbar(
          // Never saw the run end — say so rather than calling it a failure.
          result.inconclusive
            ? "The device is taking longer than usual to sync — it will finish in the background."
            : "Failed to run consolidation, please contact administrator.",
          result.inconclusive ? "warning" : "error"
        )
      );
      yield put(updateDevice({ isSynchronising: false }));
      return;
    }

    // clear the update setting error to disable the UI button; the error panel
    // hangs off migrationError, so drop that too once settings are readable.
    yield put(
      updateDevice({
        updatingSettingError: null,
        ...(readable ? { migrationError: null, consolidationError: null } : {}),
      })
    );
    yield put(openSnackbar("Consolidation has been updated", "success"));
    yield put(updateDevice({ isSynchronising: false }));
  }),
  // Queue drained while the panel still shows its error: re-read the settings
  // and clear it if they come back. A plain GET — safe for every device type.
  takeLatest(RELOAD_SETTINGS_V4, function* ({ deviceId }) {
    if (!deviceId) return;
    const settings = yield putResolve(getDeviceSettingsV4(deviceId));
    if (settings?.error) return;
    yield put(updateDevice({ migrationError: null, consolidationError: null }));
  }),
];
